import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GlobalSearch from '../components/GlobalSearch';
import V3BranchMenu from './V3BranchMenu';
import { useBranch } from '../context/BranchContext';
import { useV3Prefs } from './V3PrefsContext';
import { getV3PageTitle, V3_LAYOUT } from './v3Config';
import { Upload, Settings as SettingsIcon } from 'lucide-react';
import GlobalUploadModal from '../components/GlobalUploadModal';

// The V3 top bar: the page title where the logo used to be, the global search in the centre, and the person's menu on the
// right. It sits above the content area only, between the two edge bars. `slotRef` is the empty space on the right, between
// the search and the Upload Data button, where a page can place one extra control (Transport puts its Map View / View All button there).
const V3TopBar = ({ user, onLogout, slotRef }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { width } = useV3Prefs();
  const {
    getEffectiveBranchId,
    setViewAs,
    canSwitchBranches,
    getAccessibleBranches,
    getEffectiveBranch,
  } = useBranch();

  const accessibleBranches = getAccessibleBranches();
  const effectiveBranch = getEffectiveBranch();
  const isHomePage = location.pathname === '/' || location.pathname === '';
  const title = getV3PageTitle(location.pathname);
  const isPhone = width < V3_LAYOUT.phoneBreakpoint;

  // The bar measures its own width (in the page's own pixels, so it is right at any zoom). Below 700px, on a page whose panels take
  // up the sides (Transport), the page title is hidden so the search, the page's extra button and the icon buttons all still fit.
  const topRef = useRef(null);
  const [narrow, setNarrow] = useState(false);
  // Upload Data used to sit inside the Admin drop-down; it is an icon in the top bar now, for the same people (super admins and anyone with the global upload permission).
  const [showUpload, setShowUpload] = useState(false);
  const canUpload = user?.role === 'super_admin' || !!user?.can_access_global_upload;
  useEffect(() => {
    const element = topRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setNarrow(element.clientWidth > 0 && element.clientWidth < 800);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <header ref={topRef} className={`v3-top ${narrow ? 'v3-top--narrow' : ''}`} data-testid="v3-top-bar">
      <div className="v3-top-heading">
        <h1 className="v3-top-title" data-testid="v3-page-title">{title}</h1>
        {/* On Home the branch is part of the title ("Home C30"). A person who can switch branch presses it to get the branch list; everyone else just sees it. */}
        {isHomePage && effectiveBranch && (
          <V3BranchMenu
            branches={accessibleBranches}
            currentId={getEffectiveBranchId()}
            label={effectiveBranch.code || effectiveBranch.id}
            canSwitch={!!canSwitchBranches && accessibleBranches.length > 0}
            onPick={setViewAs}
          />
        )}
      </div>
      <div className="v3-top-search">
        {isPhone
          ? <GlobalSearch isMobile={true} user={user} />
          : <GlobalSearch user={user} dropdownTop={V3_LAYOUT.topbar} />}
      </div>
      <div className="v3-top-right">
        <div className="v3-top-extra" ref={slotRef} data-testid="v3-top-extra" />
        {/* The Light / Dark button has moved into My preferences (the user circle at the foot of the left bar). */}
        {canUpload && (
          <button
            type="button"
            className="v3-btn v3-btn--glass v3-btn--icon"
            onClick={() => setShowUpload(true)}
            title="Upload data"
            aria-label="Upload data"
            data-testid="v3-upload-data-btn"
          >
            <Upload className="v3-i" />
          </button>
        )}
        {/* The settings icon goes straight to the Settings page (for the people who may open it). Branch switching is on the Home title and Log out is in My preferences. */}
        {(user?.role === 'super_admin' || user?.can_access_settings) && (
          <button
            type="button"
            className="v3-btn v3-btn--glass v3-btn--icon"
            onClick={() => navigate('/settings')}
            title="Settings"
            aria-label="Settings"
            data-testid="header-settings-menu-btn"
          >
            <SettingsIcon className="v3-i" />
          </button>
        )}
      </div>
      {showUpload && <GlobalUploadModal isOpen={showUpload} onClose={() => setShowUpload(false)} user={user} />}
    </header>
  );
};

export default V3TopBar;
